import { z } from 'zod';
import { PRODUKT_STATUS } from './produkte';

/**
 * Channels: Verkaufskanäle und Feeds je Marktplatz (lib_channels.php der PHP-Middleware).
 * weg = wie die Daten zum Kanal kommen: feed (Kanal ruft eine feste Adresse ab) oder api/mirakl/shopify (Übertragung).
 */
export const KANAL_WEGE = ['feed', 'api', 'mirakl', 'shopify'] as const;
export type KanalWeg = (typeof KANAL_WEGE)[number];

export const KANAL_FORMATE = ['csv', 'tsv', 'xml'] as const;
export type KanalFormat = (typeof KANAL_FORMATE)[number];
export const KANAL_FORMAT_TEXT: Record<KanalFormat, string> = { csv: 'CSV', tsv: 'TSV (Tabulator)', xml: 'XML (Google-RSS)' };

export interface KanalZugangsfeld {
  schluessel: string;
  label: string;
  art: 'text' | 'geheim' | 'auswahl';
  optionen?: Record<string, string>;
  platzhalter?: string;
}

export interface KanalTypInfo {
  label: string;
  gruppe: 'Marktplatz' | 'Preisvergleich' | 'Werbung' | 'Shop' | 'Feed';
  text: string;
  weg: KanalWeg;
  zugang: readonly KanalZugangsfeld[];
  hilfe: string;
  pflicht: readonly string[];
  optional: readonly string[];
  format: KanalFormat;
  /** Preis mit Währung ausgeben („49.90 EUR“) – Google und Meta verlangen das */
  preisMitWaehrung?: boolean;
  bild: { min: number; quadrat: boolean; weiss: boolean };
}

/** Kanal-Katalog wie ch_catalog() der PHP-Middleware */
export const KANAL_TYPEN = {
  amazon: {
    label: 'Amazon Seller Central',
    gruppe: 'Marktplatz',
    text: 'Direkte Übertragung per Selling Partner API.',
    weg: 'api',
    zugang: [
      { schluessel: 'client_id', label: 'LWA Client-ID', art: 'text' },
      { schluessel: 'client_secret', label: 'LWA Client-Secret', art: 'geheim' },
      { schluessel: 'refresh_token', label: 'Refresh-Token', art: 'geheim' },
      { schluessel: 'region', label: 'Region', art: 'auswahl', optionen: { eu: 'Europa', na: 'Nordamerika', fe: 'Fernost' } },
    ],
    hilfe: 'Seller Central → Apps & Services → Develop Apps: App anlegen, autorisieren, Werte übernehmen.',
    pflicht: ['sku', 'title', 'ean', 'price', 'quantity', 'brand', 'bullets', 'main_image'],
    optional: ['description', 'material', 'farbe', 'groesse', 'gewicht_kg', 'bullet2', 'bullet3'],
    format: 'tsv',
    bild: { min: 1000, quadrat: true, weiss: true },
  },
  zalando: {
    label: 'Zalando',
    gruppe: 'Marktplatz',
    text: 'Partnerprogramm über zDirect. Produktdaten als Feed, Bestand und Preis per API.',
    weg: 'feed',
    zugang: [
      { schluessel: 'merchant_id', label: 'Merchant-ID', art: 'text' },
      { schluessel: 'client_id', label: 'zDirect Client-ID', art: 'text' },
      { schluessel: 'client_secret', label: 'zDirect Client-Secret', art: 'geheim' },
    ],
    hilfe: 'Zugang erfolgt nach Aufnahme ins Zalando Partnerprogramm (zDirect).',
    pflicht: ['sku', 'ean', 'title', 'brand', 'price', 'quantity', 'farbe', 'groesse', 'main_image', 'material'],
    optional: ['description', 'zielgruppe', 'bullets'],
    format: 'csv',
    bild: { min: 1524, quadrat: false, weiss: true },
  },
  aboutyou: {
    label: 'About You',
    gruppe: 'Marktplatz',
    text: 'Anbindung über das About You Seller Center.',
    weg: 'feed',
    zugang: [
      { schluessel: 'api_key', label: 'Seller-Center API-Key', art: 'geheim' },
      { schluessel: 'shop_id', label: 'Shop-ID', art: 'text' },
    ],
    hilfe: 'API-Key im About You Seller Center unter Einstellungen → API.',
    pflicht: ['sku', 'ean', 'title', 'brand', 'price', 'quantity', 'farbe', 'groesse', 'main_image'],
    optional: ['description', 'material', 'zielgruppe'],
    format: 'csv',
    bild: { min: 1200, quadrat: false, weiss: false },
  },
  decathlon: {
    label: 'Decathlon',
    gruppe: 'Marktplatz',
    text: 'Marketplace auf Mirakl-Basis. Angebote per Mirakl-API.',
    weg: 'mirakl',
    zugang: [
      { schluessel: 'mirakl_url', label: 'Mirakl-URL', art: 'text', platzhalter: 'https://marketplace-decathlon-eu.mirakl.net' },
      { schluessel: 'api_key', label: 'Shop-API-Key', art: 'geheim' },
      { schluessel: 'shop_id', label: 'Shop-ID', art: 'text' },
    ],
    hilfe: 'Im Mirakl-Backoffice: Mein Benutzerkonto → API-Schlüssel.',
    pflicht: ['sku', 'ean', 'price', 'quantity'],
    optional: ['title', 'description', 'brand', 'main_image'],
    format: 'csv',
    bild: { min: 1000, quadrat: false, weiss: true },
  },
  idealo: {
    label: 'idealo',
    gruppe: 'Preisvergleich',
    text: 'Preisvergleich. idealo ruft einen Feed unter fester Adresse ab.',
    weg: 'feed',
    zugang: [{ schluessel: 'shop_id', label: 'idealo Shop-ID', art: 'text' }],
    hilfe: 'Feed-Adresse im idealo Business-Portal hinterlegen.',
    pflicht: ['sku', 'title', 'price', 'link', 'image_link', 'ean', 'brand', 'lieferzeit', 'versandkosten'],
    optional: ['description', 'product_type'],
    format: 'csv',
    bild: { min: 500, quadrat: false, weiss: false },
  },
  google: {
    label: 'Google Merchant Center',
    gruppe: 'Werbung',
    text: 'Google Shopping. Feed unter fester Adresse.',
    weg: 'feed',
    zugang: [],
    hilfe: 'Feed-Adresse im Merchant Center als geplanten Abruf hinterlegen.',
    pflicht: ['id', 'title', 'description', 'link', 'image_link', 'availability', 'price', 'brand', 'gtin'],
    optional: ['condition', 'product_type', 'google_product_category', 'shipping_weight', 'item_group_id'],
    format: 'xml',
    preisMitWaehrung: true,
    bild: { min: 800, quadrat: false, weiss: false },
  },
  meta: {
    label: 'Meta / Facebook',
    gruppe: 'Werbung',
    text: 'Katalog für Facebook- und Instagram-Shops.',
    weg: 'feed',
    zugang: [],
    hilfe: 'Feed-Adresse im Commerce Manager als Datenquelle hinterlegen.',
    pflicht: ['id', 'title', 'description', 'availability', 'condition', 'price', 'link', 'image_link', 'brand'],
    optional: ['product_type', 'google_product_category', 'sale_price', 'item_group_id'],
    format: 'xml',
    preisMitWaehrung: true,
    bild: { min: 600, quadrat: false, weiss: false },
  },
  shopify: {
    label: 'Shopify-Shop',
    gruppe: 'Shop',
    text: 'Weiterer Shopify-Shop (z. B. Länder- oder B2B-Shop). Produkte als Entwurf.',
    weg: 'shopify',
    zugang: [
      { schluessel: 'shop', label: 'Shop-Domain', art: 'text', platzhalter: 'name.myshopify.com' },
      { schluessel: 'token', label: 'Admin-API Access-Token', art: 'geheim' },
    ],
    hilfe: 'Im Ziel-Shop eine App mit write_products anlegen und den Token übernehmen.',
    pflicht: ['sku', 'title', 'price'],
    optional: ['description', 'ean', 'brand', 'main_image', 'product_type'],
    format: 'csv',
    bild: { min: 800, quadrat: false, weiss: false },
  },
  custom: {
    label: 'Eigener Feed',
    gruppe: 'Feed',
    text: 'Frei konfigurierbarer CSV-, TSV- oder XML-Feed für beliebige Abnehmer.',
    weg: 'feed',
    zugang: [],
    hilfe: 'Feed-Adresse an den Abnehmer weitergeben.',
    pflicht: ['id', 'title', 'price'],
    optional: ['description', 'image_link', 'gtin', 'brand', 'link', 'quantity', 'availability'],
    format: 'csv',
    bild: { min: 500, quadrat: false, weiss: false },
  },
} as const satisfies Record<string, KanalTypInfo>;
export type KanalTyp = keyof typeof KANAL_TYPEN;
export const KANAL_TYP_LISTE = Object.keys(KANAL_TYPEN) as KanalTyp[];
export const kanalTyp = (t: string): KanalTypInfo => (KANAL_TYPEN as Record<string, KanalTypInfo>)[t] ?? KANAL_TYPEN.custom;
export const kanalFelder = (t: string): string[] => {
  const s = kanalTyp(t);
  return [...s.pflicht, ...s.optional];
};

/** Quellfelder aus Produkt und Variante. Dazu kommen „optionen.<Name>“ (z. B. optionen.Farbe) aus den Varianten. */
export const KANAL_QUELLFELDER = {
  produkt_id: 'Produkt-ID',
  variante_id: 'Varianten-ID',
  sku: 'SKU',
  ean: 'EAN / GTIN',
  titel: 'Titel',
  titel_mit_variante: 'Titel mit Variante',
  variante: 'Variante',
  beschreibung: 'Beschreibung (Text)',
  beschreibung_html: 'Beschreibung (HTML)',
  marke: 'Marke',
  kategorie: 'Kategorie',
  haendler: 'Händler',
  preis: 'Preis',
  preis_alt: 'Streichpreis',
  bestand: 'Bestand',
  verfuegbarkeit: 'Verfügbarkeit (in_stock / out_of_stock)',
  bild_1: 'Bild 1',
  bild_2: 'Bild 2',
  bild_3: 'Bild 3',
  bilder: 'Alle Bilder (Komma)',
  link: 'Produktlink (aus Link-Vorlage)',
} as const;
export type KanalQuellfeld = keyof typeof KANAL_QUELLFELDER;

/** Vorbelegung der Zuordnung, damit ein neuer Kanal sofort einen brauchbaren Feed liefert */
const STANDARD_QUELLE: Record<string, string> = {
  id: 'sku',
  sku: 'sku',
  title: 'titel_mit_variante',
  ean: 'ean',
  gtin: 'ean',
  price: 'preis',
  quantity: 'bestand',
  brand: 'marke',
  description: 'beschreibung',
  main_image: 'bild_1',
  image_link: 'bild_1',
  link: 'link',
  availability: 'verfuegbarkeit',
  product_type: 'kategorie',
  farbe: 'optionen.Farbe',
  groesse: 'optionen.Größe',
  item_group_id: 'produkt_id',
};
const STANDARD_FESTWERT: Record<string, string> = { condition: 'new' };

export function standardZuordnung(typ: string): { zuordnung: Record<string, string>; festwerte: Record<string, string> } {
  const zuordnung: Record<string, string> = {};
  const festwerte: Record<string, string> = {};
  for (const f of kanalFelder(typ)) {
    if (STANDARD_QUELLE[f]) zuordnung[f] = STANDARD_QUELLE[f];
    if (STANDARD_FESTWERT[f]) festwerte[f] = STANDARD_FESTWERT[f];
  }
  return { zuordnung, festwerte };
}

export const KANAL_ZEITPLAENE = ['aus', 'taeglich', 'stuendlich'] as const;
export type KanalZeitplan = (typeof KANAL_ZEITPLAENE)[number];
export const KANAL_ZEITPLAN_TEXT: Record<KanalZeitplan, string> = { aus: 'Aus', taeglich: 'Täglich', stuendlich: 'Stündlich' };

export const KANAL_AUSWAHL_MODI = ['alle', 'kategorien', 'haendler', 'produkte'] as const;
export type KanalAuswahlModus = (typeof KANAL_AUSWAHL_MODI)[number];

/** Grenzen der Auswahllisten – die Auswahl steht als JSON im Kanal */
export const KANAL_MAX_KATEGORIEN = 500;
export const KANAL_MAX_HAENDLER = 500;
export const KANAL_MAX_PRODUKTE = 5000;

const Uuid = z.string().uuid();

export const KanalAuswahlSchema = z.object({
  modus: z.enum(KANAL_AUSWAHL_MODI).default('alle'),
  kategorien: z.array(z.string().trim().max(120)).max(KANAL_MAX_KATEGORIEN).default([]),
  haendler: z.array(Uuid).max(KANAL_MAX_HAENDLER).default([]),
  produkte: z.array(Uuid).max(KANAL_MAX_PRODUKTE).default([]),
  /** Produktstatus, die in den Feed gehen */
  status: z.array(z.enum(PRODUKT_STATUS)).min(1, 'Bitte mindestens einen Status wählen').default(['freigegeben', 'gelistet']),
  nurMitBestand: z.boolean().default(false),
  nurOhneMaengel: z.boolean().default(false),
});
export type KanalAuswahl = z.infer<typeof KanalAuswahlSchema>;
export const standardAuswahl = (): KanalAuswahl => KanalAuswahlSchema.parse({});

const Feldname = z.string().regex(/^[a-z][a-z0-9_]{0,40}$/);
const Quelle = z.string().max(120).regex(/^(|[a-z_0-9]+|optionen\.[^\s].{0,80})$/, 'Unbekanntes Quellfeld');

export const KanalAnlegenSchema = z.object({
  typ: z.enum(KANAL_TYP_LISTE as [KanalTyp, ...KanalTyp[]]),
  name: z.string().trim().max(80).optional(),
});
export type KanalAnlegenEingabe = z.input<typeof KanalAnlegenSchema>;

export const KanalSpeichernSchema = z.object({
  name: z.string().trim().min(1, 'Bitte einen Namen angeben').max(80),
  format: z.enum(KANAL_FORMATE),
  aktiv: z.boolean(),
  auswahl: KanalAuswahlSchema,
  zuordnung: z.record(Feldname, Quelle).default({}),
  festwerte: z.record(Feldname, z.string().max(500)).default({}),
  linkVorlage: z
    .string()
    .trim()
    .max(300)
    .refine((v) => v === '' || /^https:\/\/[^\s]+$/.test(v), 'Bitte eine https-Adresse angeben, z. B. https://shop.beispiel.de/products/{handle}')
    .default(''),
  /** Zugangsfelder: leer oder „••…“ = unverändert lassen (geheime Felder) */
  zugang: z.record(z.string().max(40), z.string().max(4000)).default({}),
  zeitplan: z.enum(KANAL_ZEITPLAENE).default('aus'),
  zeitplanStunde: z.number().int().min(0).max(23).default(3),
});
export type KanalSpeichernEingabe = z.input<typeof KanalSpeichernSchema>;

export const KanalAbfrage = z.object({
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

export interface KanalZeile {
  id: string;
  typ: KanalTyp;
  name: string;
  format: KanalFormat;
  aktiv: boolean;
  weg: KanalWeg;
  /** alle Zugangsfelder ausgefüllt (bzw. Kanal braucht keine) */
  verbunden: boolean;
  geprueftAm: string | null;
  feedLinkAktiv: boolean;
  zeitplan: KanalZeitplan;
  naechsterLauf: string | null;
  letzterLauf: string | null;
  letzterStatus: 'ok' | 'fehler' | null;
  letzteAnzahl: number | null;
  letzterAbruf: string | null;
}

export interface KanalSeite {
  eintraege: KanalZeile[];
  weiter: string | null;
}

export type KanalZugangStand = Record<string, { gesetzt: boolean; wert?: string }>;

export interface KanalDetail extends KanalZeile {
  auswahl: KanalAuswahl;
  zuordnung: Record<string, string>;
  festwerte: Record<string, string>;
  linkVorlage: string;
  /** geheime Felder nur als „gesetzt“, nie im Klartext */
  zugang: KanalZugangStand;
  zeitplanStunde: number;
  feedErstelltAm: string | null;
  abrufe: number;
  letzteMeldung: string;
  letzteUebertragungId: string | null;
  erstelltAm: string;
  geaendertAm: string;
}

/** Antwort beim Anlegen und beim Erneuern des Feed-Links – der Link steht nur hier im Klartext */
export interface KanalFeedLink {
  url: string;
}

export interface KanalTestErgebnis {
  ok: boolean;
  info: string | null;
  fehler: string | null;
}

export interface KanalVorschau {
  spalten: string[];
  zeilen: Record<string, string>[];
  /** Anfang der Datei, wie der Kanal sie erhält */
  text: string;
}

export interface KanalLuecken {
  geprueft: number;
  bereit: number;
  mitLuecken: number;
  /** alle Zeilen der Auswahl geprüft (sonst nur die ersten KANAL_LUECKEN_GRENZE) */
  vollstaendig: boolean;
  felder: Record<string, number>;
  beispiele: { produktId: string; sku: string; titel: string; fehlt: string[] }[];
}

export interface KanalLaufErgebnis {
  ok: boolean;
  zeilen: number;
  meldung: string;
}

export interface KanalQuellfelder {
  felder: { schluessel: string; label: string }[];
}

/* ---------------------------------------------------------------- KI-Prompts */

export const KANAL_PROMPT_ARTEN = ['text', 'bild'] as const;
export type KanalPromptArt = (typeof KANAL_PROMPT_ARTEN)[number];
export const KANAL_MAX_PROMPTS = 50;

export const KanalPromptSchema = z.object({
  name: z.string().trim().min(1, 'Bitte einen Namen angeben').max(80),
  art: z.enum(KANAL_PROMPT_ARTEN),
  /** Zielfeld im Kanal (z. B. title, bullets) oder „bild“ */
  ziel: z.string().trim().max(40).default(''),
  text: z.string().max(8000).refine((t) => t.trim().length > 0, 'Bitte einen Prompt eingeben'),
});
export type KanalPrompt = z.infer<typeof KanalPromptSchema>;
export const KanalPromptsSchema = z.object({ prompts: z.array(KanalPromptSchema).max(KANAL_MAX_PROMPTS) });

export const KanalPromptVorschauSchema = z.object({ text: z.string().max(8000), produktId: z.string().uuid().optional() });

/** Vorlagen der PHP-Middleware (ch_default_prompts) – gelten, solange die Bibliothek leer ist */
export const KANAL_STANDARD_PROMPTS: KanalPrompt[] = [
  {
    name: 'Titel für Amazon',
    art: 'text',
    ziel: 'title',
    text: 'Schreibe einen Amazon-konformen Produkttitel auf Deutsch.\nRegeln: max. 200 Zeichen, Schema „Marke + Produkttyp + Hauptmerkmal + Größe/Menge“, keine Werbeaussagen, keine Sonderzeichen außer Bindestrich, keine Preise.\nProduktdaten:\n{{produkt}}',
  },
  {
    name: 'Bulletpoints (5 Stück)',
    art: 'text',
    ziel: 'bullets',
    text: 'Schreibe genau 5 Bulletpoints auf Deutsch für ein Amazon-Listing.\nRegeln: je 150–200 Zeichen, beginnt mit einem Nutzen in GROSSBUCHSTABEN gefolgt von Doppelpunkt, dann die Erklärung. Keine erfundenen technischen Daten.\nProduktdaten:\n{{produkt}}',
  },
  {
    name: 'Produktbeschreibung SEO',
    art: 'text',
    ziel: 'description',
    text: 'Schreibe eine SEO-Produktbeschreibung auf Deutsch, 700–1100 Zeichen, 2–3 Absätze, reiner Text ohne HTML.\nNenne Material, Passform, Pflegehinweise und Besonderheiten. Erfinde keine Daten.\nProduktdaten:\n{{produkt}}',
  },
  {
    name: 'Attribute ableiten',
    art: 'text',
    ziel: 'attribute',
    text: 'Leite aus den Produktdaten die folgenden Attribute ab, soweit sie eindeutig hervorgehen: material, farbe, groesse, passform, geschlecht, zielgruppe.\nGib für nicht erkennbare Attribute einen leeren Wert zurück. Erfinde nichts.\nProduktdaten:\n{{produkt}}',
  },
  {
    name: 'Hauptbild freistellen',
    art: 'bild',
    ziel: 'bild',
    text: 'Produktfoto freistellen: reinweißer Hintergrund (RGB 255,255,255), Produkt zentriert, füllt 85 % der Bildfläche, keine Schatten am Rand, kein Text, kein Wasserzeichen, quadratisch 1:1.',
  },
  {
    name: 'Lifestyle-Szene',
    art: 'bild',
    ziel: 'bild',
    text: 'Lifestyle-Motiv: Produkt in einer hellen, urbanen Umgebung oder auf neutralem Studiohintergrund, weiches Tageslicht, natürliche Farben, Produkt klar im Fokus, Farben und Material originalgetreu, kein Text.',
  },
];

export interface KanalPromptListe {
  prompts: KanalPrompt[];
  /** true = Vorlagen, noch nichts gespeichert */
  standard: boolean;
}

export interface KanalPromptVorschau {
  text: string;
}
